import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import ErrorBoundaryWrapper from 'components/ErrorBoundaryWrapper';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Error from 'components/Error';
import Loader from 'components/Loader';
import { Body } from 'components/Typography';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getPathWithRef, getUrlParams, scrollToTop } from 'helpers/utils';
import {
  democracyEngineCheckoutRedirect,
  getDonationSelectionsObj,
  getProcessingRedirect,
  getUrlSearchParamsStrWithoutRecipients,
  saveDonationSelectionsFromUrl,
} from 'helpers/donate';
import { useAuth } from 'hooks/auth';
import { getPayData } from 'selectors/pay';
import { getDonatePageData } from 'selectors/donatePage';
import { getUserState } from 'selectors/user';
import { getPaymentsData, getPaymentsState } from 'selectors/payments';
import {
  clearDonationSelections,
  clearEventPageSelections,
  getRedirectAfterLogin,
} from 'services/storage';
import { getDonatePage } from 'store/donatePage/actions';
import { getUserProfile } from 'store/user/actions';
import { getPayments } from 'store/payments/actions';
import { ProcessingRedirectOptions } from 'store/donatePage/constants';
import { DECheckoutFlow } from './DECheckoutFlow';
import { DEConfirmation } from './DEConfirmation';
import { trackDECheckoutViewed } from 'helpers/mixpanelEvents';
import { getRecipientsData } from 'selectors/recipients';
import { getRecipients } from 'store/recipients/actions';
import { trackPixel } from 'helpers/pixel';
import { getEventSelectionsObj } from 'helpers/event';
import { getEventData } from 'selectors/eventPage';
import { getEvent } from 'store/eventPage/actions';
import { IPage } from 'store/donatePage/types';
import { IEvent } from 'store/eventPage/types';
import HeaderAndFooterLayout from 'layouts/HeaderAndFooter';
import { getPageImageUrl } from 'helpers/imageUrls';
import { datadogLogs } from '@datadog/browser-logs';
import { RecipientsRequestType } from 'store/recipients/types';

const beforeUnloadHandler = (event: Event) => {
  // Recommended
  event.preventDefault();

  // Included for legacy support, e.g. Chrome/Edge < 119
  event.returnValue = true;
};

const isRemainingInDonateFlow = () => {
  return (
    (window.location.pathname === '/login' && getRedirectAfterLogin()) ||
    window.location.pathname === '/donate' ||
    window.location.pathname === '/checkout'
  );
};

export const DECheckoutScreen = () => {
  const { urlPartner, urlEvent, urlShoppingCart } = getUrlParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isLoading: isAuthLoading, isAuthenticated } = useAuth0();
  const [processingRedirect, setProcessingRedirect] = useState(ProcessingRedirectOptions.DE_OATH);
  const [donationAmount, setDonationAmount] = useState<number>(0);
  const [candidates, setCandidates] = useState<CandidateType[]>([]);
  const [shouldRedirectToDE, setShouldRedirectToDE] = useState(false);
  const [areDonationSelectionsReady, setAreDonationSelectionsReady] = useState(false);
  const [eventSelections, setEventSelections] = useState();
  const [pageData, setPageData] = useState<IPage | IEvent | undefined>();
  const { donatePageData, eventData, payment, payments, paymentsState, recipients, userState } =
    useAppSelector(state => ({
      donatePageData: getDonatePageData(state),
      eventData: getEventData(state),
      payment: getPayData(state),
      payments: getPaymentsData(state),
      paymentsState: getPaymentsState(state),
      recipients: getRecipientsData(state),
      userState: getUserState(state),
    }));
  const isCheckoutV2Enabled = true;
  const requestLocationPath = location.pathname;

  const redirectToDE = (candidates, donationAmount) => {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
    const { candidates: storedCandidates, total: storedDonationAmount } =
      getDonationSelectionsObj();
    const savedCandidates = candidates || storedCandidates;
    const savedDonationAmount = donationAmount || storedDonationAmount;
    if (!savedCandidates?.length || !savedDonationAmount) {
      navigate(getPathWithRef(`/donate?${getUrlSearchParamsStrWithoutRecipients()}`));
      return;
    }
    clearDonationSelections();
    democracyEngineCheckoutRedirect(savedCandidates, savedDonationAmount);
    return;
  };

  const handleNewDonationSelections = () => {
    const { candidates: savedCandidates, total: savedDonationAmount } = getDonationSelectionsObj();
    if (savedCandidates) {
      setCandidates(savedCandidates);
    }
    if (savedDonationAmount) {
      setDonationAmount(Number(savedDonationAmount));
    }
    if (!savedCandidates || !savedDonationAmount) {
      navigate(getPathWithRef(`/donate?${getUrlSearchParamsStrWithoutRecipients()}`));
    }
  };

  useAuth(() => {
    dispatch(getUserProfile());
    dispatch(getPayments());
  });

  useEffect(() => {
    if (candidates && donationAmount && shouldRedirectToDE) {
      redirectToDE(candidates, donationAmount);
    }
  }, [candidates, donationAmount, shouldRedirectToDE]);

  useEffect(() => {
    if (urlEvent) {
      dispatch(getEvent({ urlPartner }));
    } else if (
      (!donatePageData ||
        (donatePageData.query_str !== urlPartner && urlPartner !== 'undefined')) &&
      urlPartner
    ) {
      dispatch(getDonatePage({ urlPartner }));
    }
    scrollToTop();
    trackPixel('universalPixel');
  }, []);

  useEffect(() => {
    setEventSelections(getEventSelectionsObj());
  }, []);

  useEffect(() => {
    if (urlEvent && eventData) {
      setPageData(eventData);
    } else if (!urlEvent && donatePageData && urlPartner) {
      setPageData(donatePageData);
    } else if (!urlPartner) {
      // no page data for search page
      setPageData(undefined);
    }
  }, [donatePageData, eventData]);

  useEffect(() => {
    if (!recipients?.length && (pageData || !urlPartner)) {
      let recipientIds: string[] | undefined;
      if (urlShoppingCart?.length) {
        recipientIds = urlShoppingCart;
      } else if (urlPartner && pageData) {
        recipientIds = pageData.recipient_ids || [];
      }
      dispatch(
        getRecipients({
          amount: 200,
          recipient_ids: recipientIds,
          ...(urlPartner &&
            pageData?.processing_redirect && {
              payment_processor: pageData.processing_redirect,
            }),
          ...(urlPartner &&
            pageData?.recipient_order_by && {
              order_by: pageData?.recipient_order_by,
            }),
          request_location_path: requestLocationPath,
          request_type: RecipientsRequestType.checkout,
          page_query_str: urlPartner,
        })
      );
    }
    if (pageData && pageData.processing_redirect === ProcessingRedirectOptions.STRIPE) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      clearDonationSelections();
      datadogLogs.logger.error(
        'User attempting to view DE checkout for Stripe page. Redirecting to donate page.',
        {
          page_query_string: pageData?.query_str,
          referrer: document.referrer,
        }
      );
      // Hard redirect to donate page to avoid potential issues with checking out with DE on a Stripe page
      window.location.href = getPathWithRef(`/donate?${getUrlSearchParamsStrWithoutRecipients()}`);
      return;
    }
  }, [pageData]);

  useEffect(() => {
    if (
      !payment &&
      !isAuthLoading &&
      (isCheckoutV2Enabled || payments?.length || isAuthenticated)
    ) {
      window.addEventListener('beforeunload', beforeUnloadHandler);
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    }

    return () => {
      if (!isRemainingInDonateFlow()) {
        clearDonationSelections();
        clearEventPageSelections();
      }
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [isAuthLoading, isAuthenticated, payments, payment, candidates, donationAmount]);

  useEffect(() => {
    if (recipients?.length > 0 && !areDonationSelectionsReady) {
      saveDonationSelectionsFromUrl(recipients);
      setAreDonationSelectionsReady(true);
    }
  }, [recipients, areDonationSelectionsReady]);

  useEffect(() => {
    if (!areDonationSelectionsReady) {
      return;
    }
    handleNewDonationSelections();
  }, [areDonationSelectionsReady]);

  useEffect(() => {
    if (
      !isCheckoutV2Enabled &&
      (payments?.length === 0 || paymentsState.error || userState.error)
    ) {
      setShouldRedirectToDE(true);
    }
  }, [payments, paymentsState.error, userState.error]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }
    if (!isAuthenticated && !isCheckoutV2Enabled) {
      setShouldRedirectToDE(true);
      return;
    }
    if (!userState.data && !isCheckoutV2Enabled) {
      return;
    }
    if (userState?.data?.id || isCheckoutV2Enabled) {
      trackDECheckoutViewed({
        paymentProcessor: 'DE',
        userId: userState?.data?.id || '',
        candidates,
        donationAmount,
      });
      return;
    }
  }, [isAuthLoading, isAuthenticated, userState.data?.id]);

  useEffect(() => {
    if (pageData) {
      const updatedProcessingRedirect = getProcessingRedirect(pageData);
      setProcessingRedirect(updatedProcessingRedirect);
    }
  }, [pageData]);

  if (urlPartner && processingRedirect !== ProcessingRedirectOptions.DE_OATH) {
    setShouldRedirectToDE(true);
  }

  const isLoading =
    ((!pageData || pageData.processing_redirect === ProcessingRedirectOptions.STRIPE) &&
      urlPartner) ||
    (!isCheckoutV2Enabled && !payments?.length) ||
    (!isCheckoutV2Enabled && !userState?.data) ||
    isAuthLoading ||
    (isAuthenticated && !payments);

  return (
    <HeaderAndFooterLayout
      footerVisible={true}
      footerWhiteLabeled={!!pageData?.whitelabeled}
      headerVisible={!pageData || !urlPartner || !pageData.whitelabeled}
      isLoading={false}
      hasIssues={true}
    >
      {isLoading ? (
        <div className="loader-center">
          <Loader width={60} height={60} />
        </div>
      ) : (
        <Routes>
          <Route
            index
            element={
              <ErrorBoundaryWrapper
                fallback={
                  <Error
                    data={{ component: 'Checkout Flow' }}
                    errorMsg="Failure with donate Checkout Flow component"
                    shouldAlert={true}
                  >
                    <Body>We encountered an error. Please contact support.</Body>
                  </Error>
                }
              >
                <DECheckoutFlow
                  candidates={candidates}
                  clientId={pageData?.client_id}
                  donationAmount={donationAmount}
                  eventSelections={eventSelections}
                  pageOwner={pageData?.owner ?? 'Unknown'}
                  payments={payments}
                  user={userState.data}
                />
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="/confirmation"
            element={
              <ErrorBoundaryWrapper
                fallback={
                  <Error
                    data={{ component: 'Checkout Confirmation' }}
                    errorMsg="Failure with donate Checkout Confirmation component"
                    shouldAlert={true}
                  >
                    <Body>We encountered an error. Please contact support.</Body>
                  </Error>
                }
              >
                <DEConfirmation
                  candidates={candidates}
                  candidateCount={candidates.length}
                  clientId={pageData?.client_id}
                  donationAmount={donationAmount}
                  isEvent={urlEvent}
                  handleNewDonationSelections={handleNewDonationSelections}
                  pageImage={urlPartner && pageData ? getPageImageUrl(pageData) : undefined}
                  postDonateMsg={eventData?.post_donate_msg}
                />
              </ErrorBoundaryWrapper>
            }
          />
        </Routes>
      )}
    </HeaderAndFooterLayout>
  );
};
